import * as Sentry from '@sentry/react';

import config from '@/config';
import { isMobileWallet } from '@/lib/responsive';

const isDev = config.IS_DEV;
const environment = isDev ? 'development' : 'production';

const SENTRY_DSN =
  'https://d6881181dbf928e57b9ac0eb7c8378fe@o4510516128972800.ingest.us.sentry.io/4510516346748928';

export function initSentry(): void {
  if (isDev) {
    console.info('[Sentry] Skipped initialization in development');
    return;
  }

  Sentry.init({
    dsn: SENTRY_DSN,
    environment,
    release: import.meta.env.VITE_VERCEL_GIT_COMMIT_SHA || 'local-dev',
    sendDefaultPii: true, // Include IP for debugging

    integrations: [
      Sentry.browserTracingIntegration({ enableInp: true }), // Performance monitoring
      Sentry.replayIntegration({
        maskAllText: false,
        blockAllMedia: false,
        maskAllInputs: true,
      }), // Session replay
    ],

    // Performance: 100% in dev, 20% in prod
    tracesSampleRate: isDev ? 1.0 : 0.2,
    // Distributed tracing to backend APIs
    tracePropagationTargets: [/^https:\/\/api\.junk\.fun/],

    // Session replay: 100% in dev, 10% in prod; always capture on error
    replaysSessionSampleRate: isDev ? 1.0 : 0.1,
    replaysOnErrorSampleRate: 1.0,

    beforeSend(event, hint) {
      const error = hint.originalException;

      // Handle both Error objects and plain objects with message property
      let errorMessage = '';
      let errorCode: number | undefined;

      if (error instanceof Error) {
        errorMessage = error.message.toLowerCase();
        // Check for error code (common in wallet errors)
        errorCode = (error as any).code;
      } else if (error && typeof error === 'object') {
        // Handle plain objects (e.g., { message: "...", code: 4001 })
        const obj = error as Record<string, unknown>;
        if (typeof obj.message === 'string') {
          errorMessage = obj.message.toLowerCase();
        }
        if (typeof obj.code === 'number') {
          errorCode = obj.code;
        }
      }

      // Skip wallet rejection errors by code (EIP-1193 standard)
      // 4001 = User rejected the request
      // 4100 = Unauthorized
      // 4200 = Unsupported method
      if (errorCode === 4001 || errorCode === 4100) {
        return null;
      }

      if (errorMessage) {
        // Skip user-initiated rejections (includes localized wallet messages)
        const rejectionPatterns = [
          'user rejected',
          'user denied',
          'rejected the request',
          'cancelled',
          'canceled',
          'approval denied',          // Phantom mobile
          'anda membatalkan',         // Indonesian (you cancelled)
          'transaksi dibatalkan',     // Indonesian (transaction cancelled)
          'đã hủy giao dịch',         // Vietnamese
          'transaction cancelled',
          'transaction canceled',
          '用户拒绝',                  // Chinese
          '사용자가 거부',              // Korean
          'usuario rechazó',          // Spanish
          'пользователь отклонил',     // Russian
          'proposal expired',         // WalletConnect timeout
        ];
        if (rejectionPatterns.some(pattern => errorMessage.includes(pattern))) {
          return null;
        }

        // Skip expected user-actionable errors
        if (
          errorMessage.includes('wallet not connected') ||
          errorMessage.includes('wallet provider not available') ||
          errorMessage.includes('authentication required')
        ) {
          return null;
        }

        // Skip WalletConnect external errors
        if (
          errorMessage.includes('method not found') ||
          errorMessage.includes('unsupported method')
        ) {
          return null;
        }

        // Skip browser extension DOM manipulation errors (not our code)
        if (
          errorMessage.includes('removechild') ||
          errorMessage.includes('insertbefore') ||
          errorMessage.includes('the node to be removed is not a child')
        ) {
          return null;
        }

        // Skip stale module import errors (deployment cache issues - user should refresh)
        if (
          errorMessage.includes('failed to fetch dynamically imported module') ||
          errorMessage.includes('importing a module script failed')
        ) {
          return null;
        }
      }

      // Also check event exception values for the same patterns
      const exceptionValue = event.exception?.values?.[0]?.value?.toLowerCase() || '';
      if (exceptionValue) {
        // Skip "Object captured as exception" with user rejection context
        if (
          exceptionValue.includes('object captured as exception') ||
          exceptionValue.includes('approval denied') ||
          exceptionValue.includes('user rejected')
        ) {
          return null;
        }
      }

      return event;
    },

    normalizeDepth: 6, // Handle deep Solana transaction objects
  });

  console.info(`[Sentry] Initialized for ${environment}`);
}

export function setSentryUser(user: { walletAddress?: string } | null): void {
  if (user) {
    Sentry.setUser({ id: user.walletAddress, username: user.walletAddress });
  } else {
    Sentry.setUser(null);
  }
}

export function captureException(
  error: Error | unknown,
  context?: Record<string, unknown>
): void {
  Sentry.withScope(scope => {
    if (context) {
      scope.setExtras(context);
    }
    Sentry.captureException(error);
  });
}

export function createErrorContext(
  operation: string,
  walletAddress: string,
  extra?: Record<string, unknown>
): Record<string, unknown> {
  return {
    operation,
    walletAddress,
    isMobile: isMobileWallet(),
    timestamp: new Date().toISOString(),
    ...extra,
  };
}
